
import React, { useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import LetterInfoPanel from './LetterInfoPanel';
import EdgeLegend from './EdgeLegend';
import { Letter, Link } from '../../types/letterGraph';
import { getColor, getEdgeColor } from '../../utils/letterGraphUtils';
import { LetterType } from '../../types/letterGraph';

// vis-network в глобальной области
declare global { 
    interface Window { 
        vis: any;
    } 
}

interface TreeModalProps {
    selectedNodeId: number | null;
    selectedLetter: Letter | null;
    allLetters: Letter[];
    allLinks: Link[];
    highlightedNodeInModal: number | null;
    selectedLetterInModal: Letter | null;
    onClose: () => void;
    onLetterSelect: (letter: Letter) => void;
    onLetterInfoClose: () => void;
    onExportToExcel: (nodeId: number) => void;
}

const TreeModal: React.FC<TreeModalProps> = ({
    selectedNodeId,
    selectedLetter,
    allLetters,
    allLinks,
    highlightedNodeInModal,
    selectedLetterInModal,
    onClose,
    onLetterSelect,
    onLetterInfoClose,
    onExportToExcel,
}) => {
    const treeNetworkContainerRef = useRef<HTMLDivElement | null>(null);
    const treeNetworkInstanceRef = useRef<any>(null);
    const nodesDataSetRef = useRef<any>(null);
    const draggedNodeIdRef = useRef<number | null>(null);
    const savedPositionsRef = useRef<Map<number, { x: number; y: number }>>(new Map());

    // Экспорт фото связей (только граф из модального окна)
    const handleExportChainToImage = useCallback(async () => {
        if (!treeNetworkContainerRef.current || !selectedNodeId) return;
        
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const canvas = await html2canvas(treeNetworkContainerRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
            });
            
            const link = document.createElement('a');
            link.download = `Связи_${allLetters.find(l => l.id === selectedNodeId)?.number || selectedNodeId}_${new Date().toISOString().slice(0, 10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('Ошибка экспорта в изображение:', error);
            alert('Ошибка при экспорте в изображение');
        }
    }, [selectedNodeId, allLetters]);

    // Эффект для построения графа в модальном окне
    useEffect(() => {
        if (!selectedNodeId || !treeNetworkContainerRef.current || !window.vis) return;
        
        const treeNodes = new Set<number>();
        const treeEdges: Link[] = [];
        const visited = new Set<number>(); 
        
        // Рекурсивная функция для построения дерева (BFS подход)
        const queue: { nodeId: number; parentId: number | null }[] = [{ nodeId: selectedNodeId, parentId: null }];
        
        while (queue.length > 0) {
            const { nodeId, parentId } = queue.shift()!;
            
           
            if (visited.has(nodeId)) continue;
            visited.add(nodeId);
            treeNodes.add(nodeId);
            
           
            if (parentId !== null) {
                treeEdges.push({ from: parentId, to: nodeId });
            }
            
            const children = allLinks
                .filter(e => e.from === nodeId && !visited.has(e.to))
                .map(e => e.to);
            
            children.forEach(childId => {
                queue.push({ nodeId: childId, parentId: nodeId });
            });
        }
        
        const chainNodes = Array.from(treeNodes).map(id => allLetters.find(l => l.id === id)).filter(Boolean) as Letter[];
        
        const nodeTypeMap = new Map<number, LetterType>();
        chainNodes.forEach(n => {
            nodeTypeMap.set(n.id, n.letter_type);
        });
        
        // Вычисление уровня для каждого узла в дереве
        const nodeLevels = new Map<number, number>();
        const calculateLevels = (nodeId: number, level: number = 0) => {
            if (nodeLevels.has(nodeId)) return;
            nodeLevels.set(nodeId, level);
            
            treeEdges.filter(e => e.from === nodeId).forEach(e => {
                calculateLevels(e.to, level + 1);
            });
        };
        calculateLevels(selectedNodeId, 0);
        
        const nodes = new window.vis.DataSet(chainNodes.map(n => {
            const isRoot = n.id === selectedNodeId;
            const level = nodeLevels.get(n.id) || 0;
            const isHighlighted = highlightedNodeInModal === n.id;
            return {
                id: n.id,
                label: n.number,
                letter_type: n.letter_type,
                title: `${n.number}\n${new Date(n.date).toLocaleDateString('ru-RU')}`,
                level: level, 
                color: {
                    border: isHighlighted ? '#FF6B6B' : (isRoot ? '#2B7CE9' : '#666'),
                    background: getColor(n.letter_type),
                    highlight: { border: '#FF6B6B', background: getColor(n.letter_type) },
                    hover: { border: '#2B7CE9', background: getColor(n.letter_type) }
                },
                shape: 'box',
                font: { size: 12, color: '#333', face: 'Arial', bold: isRoot || isHighlighted },
                margin: 6,
                borderWidth: isHighlighted ? 4 : (isRoot ? 3 : 2),
                borderWidthSelected: isHighlighted ? 5 : 4,
                fixed: false,
            };
        }));
        
        nodesDataSetRef.current = nodes;
        
        const edges = new window.vis.DataSet(treeEdges.map(e => {
            const fromType = nodeTypeMap.get(e.from) || '';
            const toType = nodeTypeMap.get(e.to) || '';
            const edgeColor = getEdgeColor(fromType, toType);
            
            return {
                from: e.from,
                to: e.to,
                arrows: { to: { enabled: true, scaleFactor: 0.7 } },
                color: { 
                    color: edgeColor, 
                    highlight: '#2B7CE9', 
                    hover: '#2B7CE9', 
                    opacity: 0.7 
                },
                width: 2
            };
        }));
        
        const options = {
            interaction: { hover: true, tooltipDelay: 100, navigationButtons: true, keyboard: true, zoomSpeed: 0.3 },
            physics: {
                enabled: true,
                solver: 'hierarchical',
                hierarchical: {
                    direction: 'UD', 
                    sortMethod: 'directed', 
                    levelSeparation: 160,
                    nodeSpacing: 180, 
                    treeSpacing: 220, 
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true,
                    shakeTowards: 'leaves'
                },
                stabilization: { 
                    iterations: 1000, 
                    updateInterval: 25,
                    onlyDynamicEdges: false,
                    fit: true
                }
            },
            edges: { 
                smooth: {
                    type: 'straight' 
                },
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 0.8,
                        type: 'arrow'
                    }
                }
            },
            nodes: { 
                scaling: { min: 16, max: 24 },
                shape: 'box',
                font: { size: 12 }
            },
            layout: { improvedLayout: false }
        };
        
        if (treeNetworkInstanceRef.current) {
            draggedNodeIdRef.current = null;
            savedPositionsRef.current.clear();
            
            treeNetworkInstanceRef.current.setData({ nodes, edges });
            treeNetworkInstanceRef.current.setOptions(options);
            setTimeout(() => {
                treeNetworkInstanceRef.current?.fit({ animation: { duration: 500 } });
            }, 100);
        } else {
            treeNetworkInstanceRef.current = new window.vis.Network(
                treeNetworkContainerRef.current, 
                { nodes, edges }, 
                options
            );
            setTimeout(() => {
                treeNetworkInstanceRef.current?.fit({ animation: { duration: 500 } });
            }, 200);
        }
        
        // обработчик клика на узлы
        if (treeNetworkInstanceRef.current) {
            treeNetworkInstanceRef.current.off('click');
            treeNetworkInstanceRef.current.on('click', (params: any) => {
                if (params.nodes.length > 0) {
                    const nodeId = params.nodes[0];
                    const letter = allLetters.find(l => l.id === nodeId);
                    if (letter) {
                        onLetterSelect(letter);
                    }
                }
            });
            
            // обработчики перетаскивания узлов
            treeNetworkInstanceRef.current.off('dragStart');
            treeNetworkInstanceRef.current.off('dragEnd');
            
            treeNetworkInstanceRef.current.on('dragStart', (params: any) => {
                if (params.nodes.length > 0 && nodesDataSetRef.current) {
                    const nodeId = params.nodes[0];
                    draggedNodeIdRef.current = nodeId;
                    
                    // сохранение позиции всех узлов
                    const positions = treeNetworkInstanceRef.current.getPositions();
                    savedPositionsRef.current.clear();
                    Object.keys(positions).forEach((id: string) => {
                        savedPositionsRef.current.set(Number(id), positions[id]);
                    });
                    
                    // фиксация узлов, чтобы они не двигались
                    const allNodeIds = nodesDataSetRef.current.getIds() as number[];
                    allNodeIds.forEach((id: number) => {
                        if (id !== nodeId) {
                            const pos = positions[id];
                            if (pos) {
                                nodesDataSetRef.current.update({
                                    id: id,
                                    fixed: { x: true, y: true },
                                    x: pos.x,
                                    y: pos.y
                                });
                            }
                        }
                    });
                    
                    treeNetworkInstanceRef.current.setOptions({
                        physics: { enabled: false }
                    });
                }
            });
            
            treeNetworkInstanceRef.current.on('dragEnd', (params: any) => {
                if (params.nodes.length > 0 && draggedNodeIdRef.current !== null && nodesDataSetRef.current) {
                    const nodeId = draggedNodeIdRef.current;
                    
                    const positions = treeNetworkInstanceRef.current.getPositions();
                    const newPos = positions[nodeId];
                    
                    if (newPos) {
                        nodesDataSetRef.current.update({
                            id: nodeId,
                            fixed: { x: true, y: true },
                            x: newPos.x,
                            y: newPos.y
                        });
                    }
                    
                    const allNodeIds = nodesDataSetRef.current.getIds() as number[];
                    allNodeIds.forEach((id: number) => {
                        if (id !== nodeId) {
                            nodesDataSetRef.current.update({
                                id: id,
                                fixed: false
                            });
                        }
                    });
                    
                    draggedNodeIdRef.current = null;
                }
            });
        }
        
        return () => {
            if (treeNetworkInstanceRef.current) {
                treeNetworkInstanceRef.current.destroy();
                treeNetworkInstanceRef.current = null;
            }
        };
    }, [selectedNodeId, allLetters, allLinks, highlightedNodeInModal, onLetterSelect]);

    if (!selectedNodeId || !selectedLetter) return null;

    return (
        <div 
            className="certificates-filter-modal-overlay" 
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
            style={{ 
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <div 
                className="certificates-filter-modal" 
                onClick={(e) => e.stopPropagation()}
                style={{ 
                    width: '98vw', 
                    maxWidth: 1800,
                    height: '95vh', 
                    maxHeight: 1200,
                    display: 'flex', 
                    flexDirection: 'column',
                    padding: 0,
                    margin: 'auto',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}
            >
                {/* Заголовок модального окна */}
                <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
                                Дерево связей письма
                            </h3>
                            <div style={{ marginTop: 8, fontSize: 14, color: '#6b7280' }}>
                                {selectedLetter.number} • {new Date(selectedLetter.date).toLocaleDateString('ru-RU')} • {selectedLetter.letter_type}
                            </div>
                        </div>
                    </div>

                    {/* Легенда цветов связей */}
                    <EdgeLegend />

                    {/* Кнопки управления */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
                        <button
                            onClick={() => {
                                if (selectedNodeId) {
                                    onExportToExcel(selectedNodeId);
                                }
                            }}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                background: '#10b981',
                                color: '#fff',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500
                            }}
                        >
                            Экспорт в Excel
                        </button>
                        <button
                            onClick={handleExportChainToImage}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                background: '#3b82f6',
                                color: '#fff',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500
                            }}
                        >
                            Экспорт фото
                        </button>
                        <button 
                            className="certificates-filter-modal__close" 
                            onClick={onClose}
                            aria-label="Закрыть"
                            style={{ fontSize: 24, lineHeight: 1 }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Область графа и панели информации */}
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <div 
                        ref={treeNetworkContainerRef} 
                        style={{ 
                            flex: 1, 
                            height: '100%', 
                            width: selectedLetterInModal ? '60%' : '100%',
                            background: '#fff',
                            minHeight: 0,
                            borderRight: selectedLetterInModal ? '1px solid #e5e7eb' : 'none',
                            transition: 'width 0.3s ease'
                        }} 
                    />

                    {/* Панель информации о письме (если выбрано) */}
                    {selectedLetterInModal && (
                        <LetterInfoPanel 
                            letter={selectedLetterInModal} 
                            onClose={onLetterInfoClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TreeModal;
